import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { ActiveRenderEntry, RenderHistoryEntry, RenderHistorySource } from '@/types/metadata'

const MAX_ENTRIES = 200
const ACTIVE_SESSION_KEY = 'truemark-render-active'
const PENDING_RESTORE_KEY = 'truemark-render-pending-restore'

type GlobalRenderHistory = typeof globalThis & {
  __tmRenderHistoryInit?: boolean
}

export interface BeginRenderMeta {
  taskUuid?: string
  taskId?: string
  startedAt?: number
}

interface RenderHistoryState {
  entries: RenderHistoryEntry[]
  active: ActiveRenderEntry[]
  begin: (source: RenderHistorySource, context: string, meta?: BeginRenderMeta) => string
  finish: (id: string, status: 'ok' | 'error', detail?: string, meta?: BeginRenderMeta) => void
  clear: () => void
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function interruptedEntries(active: ActiveRenderEntry[]): RenderHistoryEntry[] {
  const now = Date.now()
  return active.map((row) => ({
    id: row.id,
    at: new Date().toISOString(),
    source: row.source,
    context: row.context,
    durationMs: Math.max(0, now - row.startedAt),
    status: 'error' as const,
    detail: 'прервано обновлением страницы',
  }))
}

function syncActiveSession(active: ActiveRenderEntry[]): void {
  try {
    const previewOnly = active.filter((a) => a.source === 'preview')
    if (previewOnly.length === 0) {
      sessionStorage.removeItem(ACTIVE_SESSION_KEY)
      return
    }
    sessionStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(previewOnly))
  } catch {
    // ignore quota / private mode
  }
}

function restoreInterruptedFromSession(): void {
  try {
    const raw = sessionStorage.getItem(ACTIVE_SESSION_KEY)
    if (!raw) return
    sessionStorage.removeItem(ACTIVE_SESSION_KEY)
    const active = (JSON.parse(raw) as ActiveRenderEntry[]).filter((a) => a.source === 'preview')
    if (!Array.isArray(active) || active.length === 0) return
    const stale = interruptedEntries(active)
    useRenderHistoryStore.setState((s) => ({
      entries: [...stale, ...s.entries].slice(0, MAX_ENTRIES),
    }))
  } catch {
    sessionStorage.removeItem(ACTIVE_SESSION_KEY)
  }
}

export const useRenderHistoryStore = create<RenderHistoryState>()(
  persist(
    (set, get) => ({
      entries: [],
      active: [],
      begin: (source, context, meta) => {
        if (source === 'task_km' && meta?.taskUuid) {
          const existing = get().active.find(
            (a) => a.source === 'task_km' && a.taskUuid === meta.taskUuid,
          )
          if (existing) return existing.id
        }
        const id = newId()
        const row: ActiveRenderEntry = {
          id,
          source,
          context,
          startedAt: meta?.startedAt ?? Date.now(),
          taskUuid: meta?.taskUuid,
          taskId: meta?.taskId,
        }
        set((state) => {
          const active = [row, ...state.active]
          syncActiveSession(active)
          return { active }
        })
        return id
      },
      finish: (id, status, detail, meta) => {
        let row = get().active.find((a) => a.id === id)
        let resolvedId = id
        if (!row && meta?.taskUuid) {
          const existing = get().entries.find(
            (e) =>
              e.source === 'task_km' &&
              e.context.includes(meta.taskUuid!) &&
              e.detail === 'прервано обновлением страницы',
          )
          if (existing && status === 'ok') {
            const durationMs = Date.now() - (existing.at ? Date.parse(existing.at) : Date.now())
            set((state) => ({
              entries: [
                {
                  ...existing,
                  durationMs: Math.max(existing.durationMs, durationMs),
                  status: 'ok' as const,
                  detail,
                  at: new Date().toISOString(),
                },
                ...state.entries.filter((e) => e.id !== existing.id),
              ].slice(0, MAX_ENTRIES),
            }))
            return
          }
          row = get().active.find(
            (a) => a.source === 'task_km' && a.taskUuid === meta.taskUuid,
          )
          if (row) resolvedId = row.id
        }
        if (!row) return
        const durationMs = Date.now() - row.startedAt
        const entry: RenderHistoryEntry = {
          id: row.id,
          at: new Date().toISOString(),
          source: row.source,
          context: row.context,
          durationMs,
          status,
          detail,
          taskId: row.taskId ?? meta?.taskId,
        }
        set((state) => {
          const active = state.active.filter((a) => a.id !== resolvedId)
          syncActiveSession(active)
          const withoutDupes =
            meta?.taskUuid && row.source === 'task_km'
              ? state.entries.filter(
                  (e) =>
                    !(
                      e.source === 'task_km' &&
                      e.context.includes(meta.taskUuid!) &&
                      (e.detail === 'прервано обновлением страницы' || e.status === status)
                    ),
                )
              : state.entries
          const entries = [entry, ...withoutDupes].slice(0, MAX_ENTRIES)
          return { active, entries }
        })
      },
      clear: () => set({ entries: [] }),
    }),
    {
      name: 'truemark-render-history',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ entries: state.entries }),
      merge: (persisted, current) => {
        const p = persisted as Partial<Pick<RenderHistoryState, 'entries'>> | undefined
        const pEntries = p?.entries
        const entries =
          (current.entries.length >= (pEntries?.length ?? 0) ? current.entries : pEntries) ??
          current.entries
        return {
          ...current,
          entries,
          active: current.active,
        }
      },
    },
  ),
)

function initRenderHistorySession(): void {
  const g = globalThis as GlobalRenderHistory
  if (g.__tmRenderHistoryInit || typeof window === 'undefined') return
  g.__tmRenderHistoryInit = true

  window.addEventListener('beforeunload', () => {
    const previewActive = useRenderHistoryStore
      .getState()
      .active.filter((a) => a.source === 'preview')
    if (previewActive.length > 0) {
      sessionStorage.setItem(PENDING_RESTORE_KEY, '1')
    }
  })

  if (sessionStorage.getItem(PENDING_RESTORE_KEY) !== '1') return
  sessionStorage.removeItem(PENDING_RESTORE_KEY)
  restoreInterruptedFromSession()
}

initRenderHistorySession()

export function beginRenderHistory(
  source: RenderHistorySource,
  context: string,
  meta?: BeginRenderMeta,
): string {
  return useRenderHistoryStore.getState().begin(source, context, meta)
}

export function finishRenderHistory(
  id: string,
  status: 'ok' | 'error',
  detail?: string,
  meta?: BeginRenderMeta,
): void {
  useRenderHistoryStore.getState().finish(id, status, detail, meta)
}
